import { useState, useEffect, useRef, useCallback } from 'react';
import type { ChatMessageState } from '@/types/chat';

// ─── Sentence splitting ──────────────────────────────────────────

function splitIntoBubbles(content: string, maxBubbles = 6): string[] {
  if (!content.trim()) return [];

  const paragraphs = content
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  const chunks: string[] = [];
  for (const para of paragraphs) {
    if (para.length < 120) {
      chunks.push(para);
    } else {
      const sentences = para.split(/(?<=[.!?]) +(?=[A-Z])/);
      let current = sentences[0] ?? '';
      for (let i = 1; i < sentences.length; i++) {
        if (current.length < 80) {
          current += ' ' + sentences[i];
        } else {
          chunks.push(current);
          current = sentences[i];
        }
      }
      if (current) chunks.push(current);
    }
  }

  if (chunks.length === 0) return [content];

  const merged: string[] = [];
  for (const chunk of chunks) {
    if (chunk.length < 25 && merged.length > 0) {
      merged[merged.length - 1] += '\n\n' + chunk;
    } else {
      merged.push(chunk);
    }
  }

  if (merged.length <= maxBubbles) return merged;
  const capped = merged.slice(0, maxBubbles - 1);
  capped.push(merged.slice(maxBubbles - 1).join('\n\n'));
  return capped;
}

function computeDelay(content: string): number {
  return Math.min(Math.max(content.length * 40, 400), 1800);
}

// ─── Hook ────────────────────────────────────────────────────────

interface StagedMessagesResult {
  displayMessages: ChatMessageState[];
  isDisplayTyping: boolean;
}

export function useStagedMessages(
  messages: ChatMessageState[],
  isTyping: boolean,
  isStreaming: boolean,
  conversationId: string
): StagedMessagesResult {
  const [displayMessages, setDisplayMessages] = useState<ChatMessageState[]>([]);
  const [isRevealing, setIsRevealing] = useState(false);

  const processedIdsRef = useRef<Set<string>>(new Set());
  const queueRef = useRef<ChatMessageState[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevConversationIdRef = useRef(conversationId);
  // True after a conversation switch — the next messages batch is history and should show instantly
  const expectingHistoryRef = useRef(false);
  // Tracks whether messages was empty on the previous render (to detect CLEAR_MESSAGES)
  const prevMessagesEmptyRef = useRef(messages.length === 0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const processQueue = useCallback(() => {
    timerRef.current = null;
    if (queueRef.current.length === 0) {
      setIsRevealing(false);
      return;
    }
    const bubble = queueRef.current.shift()!;
    setDisplayMessages((prev) => [...prev, bubble]);

    if (queueRef.current.length > 0) {
      const nextDelay = computeDelay(queueRef.current[0].content);
      timerRef.current = setTimeout(processQueue, nextDelay);
    } else {
      setIsRevealing(false);
    }
  }, []);

  useEffect(() => {
    const isEmpty = messages.length === 0;
    const wasEmpty = prevMessagesEmptyRef.current;
    prevMessagesEmptyRef.current = isEmpty;

    // ── Conversation switch → clear state, expect history batch next ─
    if (conversationId !== prevConversationIdRef.current) {
      prevConversationIdRef.current = conversationId;
      clearTimer();
      queueRef.current = [];
      processedIdsRef.current = new Set(messages.map((m) => m.id));
      setDisplayMessages(messages);
      setIsRevealing(false);
      // Signal that the next messages update is a history load — show instantly
      expectingHistoryRef.current = isEmpty;
      return;
    }

    // ── CLEAR_MESSAGES without a conversationId change ────────────────
    // Happens when switchConversation is called with the same ID (e.g. voice
    // session ends on the same conversation the text chat is already showing).
    // Without this branch, useStagedMessages would never set expectingHistoryRef
    // and the incoming history would go through the normal staged path, showing
    // all user messages immediately while queuing assistant messages — causing
    // them to appear out of order.
    if (isEmpty && !wasEmpty) {
      clearTimer();
      queueRef.current = [];
      processedIdsRef.current = new Set();
      setDisplayMessages([]);
      setIsRevealing(false);
      expectingHistoryRef.current = true;
      return;
    }

    // ── Find messages not yet processed ─────────────────────────────
    const newMessages = messages.filter((m) => !processedIdsRef.current.has(m.id));

    // #1908: "nothing new" is NOT "nothing to do". This hook stages COPIES of
    // rows as they arrive, so an UPDATE to a row already staged — the one that
    // matters is `sending` → `complete` on the family's own turn, dispatched by
    // useChatCore once the send POST resolves — used to return here and leave
    // the staged copy frozen at the status it was appended with.
    //
    // ChatPage renders these staged rows, not the engine's, so MessageList's
    // `streamActive` (`some(m => m.status === 'sending')`) stayed true forever.
    // That is why the suggestion-chip row could never appear on production even
    // with a well-formed `ui_chips` frame sitting in its props, and why a failed
    // send would have gone on saying "sending" rather than showing its error.
    //
    // Reconcile status in place: never add, never remove, never touch content.
    // Assistant bubbles carry derived ids (`<id>_b0`), so they find no source
    // row here and are left exactly as staged — reconciling those against the
    // source would replace one bubble with the whole reply.
    if (newMessages.length === 0) {
      setDisplayMessages((prev) => {
        const byId = new Map(messages.map((m) => [m.id, m]));
        let changed = false;
        const next = prev.map((row) => {
          const source = byId.get(row.id);
          if (!source || source.status === row.status) return row;
          changed = true;
          return { ...row, status: source.status };
        });
        return changed ? next : prev;
      });
      return;
    }

    newMessages.forEach((m) => processedIdsRef.current.add(m.id));

    // ── History batch after conversation switch → show instantly ─────
    if (expectingHistoryRef.current) {
      expectingHistoryRef.current = false;
      setDisplayMessages((prev) => [...prev, ...newMessages]);
      return;
    }

    // ── Full reload detected → show instantly ────────────────────────
    // If every message in `messages` is new to processedIdsRef, the entire
    // message list was just replaced (SET_MESSAGES). This happens on the
    // initial page load when switchConversation is called while messages was
    // already [] — the CLEAR_MESSAGES branch never fires because nothing
    // changed, so expectingHistoryRef was never set. Showing all messages
    // in-order immediately is the correct behaviour for any history load.
    if (newMessages.length === messages.length) {
      setDisplayMessages(messages);
      return;
    }

    // ── Live messages → stage assistant bubbles ──────────────────────
    for (const msg of newMessages) {
      if (msg.role !== 'assistant' || msg.status !== 'complete') {
        setDisplayMessages((prev) => [...prev, msg]);
      } else {
        const texts = splitIntoBubbles(msg.content);
        if (texts.length === 0) return;   // ← guard empty bubbles
        const bubbles: ChatMessageState[] = texts.map((text, i) => ({
          ...msg,
          id: `${msg.id}_b${i}`,
          content: text,
        }));
        queueRef.current.push(...bubbles);
      }
    }

    if (queueRef.current.length > 0 && !timerRef.current) {
      setIsRevealing(true);
      const firstDelay = computeDelay(queueRef.current[0].content);
      timerRef.current = setTimeout(processQueue, firstDelay);
    }
  }, [messages, conversationId, clearTimer, processQueue]);

  useEffect(() => clearTimer, [clearTimer]);

  const hasPendingAssistant = messages.some(
    (m) => m.role === 'assistant' && m.status === 'complete' && !processedIdsRef.current.has(m.id)
  );
  const isDisplayTyping = isTyping || isStreaming || isRevealing || hasPendingAssistant;

  return { displayMessages, isDisplayTyping };
}
